import React, { useMemo } from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import TextLink from '@components/shared/TextLink';
import { BodySM, Heading5 } from '@components/shared/TextTypes';
import { getProductLink } from '@data/products';
import { ProductData } from '@data/storefront';
import handleTextReplacement from '@utils/handleTextReplacement';
import useNavigate from '@utils/useNavigate';
import useReplacedInnerHtml from '@utils/useReplacedInnerHtml';

interface Props {
  data: Partial<ProductData>;
  textColor?: string;
  listId?: number;
}

const SimpleProductBox: React.FC<Props> = ({ data, textColor = Colors.navyBlue }) => {
  const { t } = useTranslation();
  const { navigateWrapper } = useNavigate();
  const titleRef = useReplacedInnerHtml(t(data.product_title?.text || ''));
  const descriptionRef = useReplacedInnerHtml(t(data.product_description?.text || ''));
  const titleText = handleTextReplacement(t(data.product_title?.text || ''));
  const imageAlt = handleTextReplacement(data.product_image?.alt) || titleText;
  const link = useMemo(() => getProductLink(data.product_code || '') || data.link?.text, [data]);
  const image = data?.product_image?.gatsbyImageData;

  return (
    <Wrap>
      <ShadowWrap $textColor={textColor} $background={data?.background?.text}>
        <TextWrap>
          <TitleWrap ref={titleRef} />
          <DescriptionWrap ref={descriptionRef} />
          <LinkWrap>
            {/* We use programmatic navigation because with the simple href,
            the Context is not handling the country change properly and the user gets redirected a few times. */}
            <TextLink
              to={link}
              $color={textColor}
              onClick={(e) => {
                e.preventDefault();
                navigateWrapper(link || '#');
              }}
            >
              {t('components.shop-now')}
            </TextLink>
          </LinkWrap>
        </TextWrap>
        <ImageWrap>{image ? <GatsbyImage image={image} alt={imageAlt} /> : null}</ImageWrap>
      </ShadowWrap>
    </Wrap>
  );
};

export default SimpleProductBox;

interface StyleProps {
  $textColor?: string;
  $background?: string;
}

const Wrap = styled.div`
  height: 305px;
  width: 270px;
`;

const ShadowWrap = styled.div<StyleProps>`
  display: flex;
  flex-direction: column;
  border-radius: 5px;
  background: ${(props) => props.$background || 'linear-gradient(180deg, #E5F2E3 0%, #FFFBE7 100%), #E5F2E3'};
  box-shadow: 0 2px 2px 0 rgba(0, 29, 59, 0.1);
  color: ${(props) => props.$textColor || Colors.navyBlue};
`;

const TextWrap = styled.div`
  height: 150px;
  padding: 25px 22px;
  color: inherit;
  text-align: center;
  line-height: 1;
`;

const TitleWrap = styled(Heading5)`
  color: inherit;
  text-transform: uppercase;
  margin-bottom: 5px;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const DescriptionWrap = styled(BodySM)`
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const LinkWrap = styled.div`
  margin-top: 10px;
`;

const ImageWrap = styled.div`
  height: 150px;
  width: 100%;
`;
